import { useFeatureFlagContext } from '@fluentsms/feature-flags';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../auth';
import { tokenUtility } from '../token';

/**
 * MaterialUI has to be imported due to a complicated circular dependency
 * between @fluentsms/editors, @fluentsms/graphql, @fluentsms/auth, and @fluentsms/components
 */
// eslint-disable-next-line no-restricted-imports
import { Backdrop, CircularProgress } from '@mui/material';
import IdlePrompter from '../IdlePrompter';

export type AuthenticatedRouteProps = {
  children: ReactNode;
};

export const AuthenticatedRoute = ({ children }: AuthenticatedRouteProps): ReactElement => {
  const { isAuthenticated: getIsAuthenticated, isAuthenticating: getIsAuthenticating } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const hashName = `${location.pathname}${location.search}`;
  const {
    isContextSet: isFeatureFlagContextSet,
    setUserContext: setFeatureFlagContext,
    clearUserContext: clearFeatureFlagContext,
  } = useFeatureFlagContext();
  const isAuthenticated = getIsAuthenticated();
  const isAuthenticating = getIsAuthenticating();

  useEffect(() => {
    // eslint-disable-next-line require-await
    const bootstrapLaunchDarklyAsync = async () => {
      if (isAuthenticated) {
        !isFeatureFlagContextSet &&
          setFeatureFlagContext(tokenUtility.getDecodedDetailToken(), tokenUtility.getCompleteDecodedToken());
      } else {
        clearFeatureFlagContext();
      }
    };
    bootstrapLaunchDarklyAsync();
  }, [clearFeatureFlagContext, isAuthenticated, setFeatureFlagContext, isFeatureFlagContextSet]);

  if (isAuthenticating || (isAuthenticated && !isFeatureFlagContextSet)) {
    return (
      <Backdrop role="status" aria-label="page loading" id="page-loading" data-testid="page-loading" invisible open>
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }

  if (!isAuthenticated) {
    const searchParams = pathname !== '/' ? `?${new URLSearchParams({ from: `${hashName}` }).toString()}` : '';
    return <Navigate to={`/login${searchParams}`} replace />;
  }

  // if we are authenticated then start the inactivity countdown.
  return (
    <>
      <IdlePrompter />
      {children}
    </>
  );
};

export default AuthenticatedRoute;
